import React, { JSX, useState } from "react";
import { ContentCopy, Public, FindInPage, Edit, IosShare, MoreVert, Redo, DeleteForever, ExpandMore, Dashboard } from "@mui/icons-material";
import { Box, Divider, Tooltip, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography, useTheme, Chip, Stack, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { DashboardDocument } from "components/resources/dashboards/DashboardDocument";
import ISODateTime from "components/formatting/ISODateTime";
import { Link as RouterLink } from "react-router-dom";
import Link from "@mui/material/Link";
import { ResourceActionDialog } from "components/adminResources/ResourceActionDialog";
import { useTranslation } from "react-i18next";
import DashboardEditDialog from "components/widgetParts/DashboardEditDialog";
import { useSnackbar } from "notistack";
import APIRequest, { APIError } from "system/APIRequest";
import ConfirmationDialog from "components/dialogs/ConfirmationDialog";
import { DashboardPage } from "routes/dashboard/DashboardPage";
import { TextLoader } from "components/apiHandling/TextLoader";
import dayjs from "dayjs";
import ISORelativeDateTime from "components/formatting/ISORelativeDateTime";
import { currentUser } from "system/InfegyUser";
import StarIcon from '@mui/icons-material/Star';
import HistoryIcon from '@mui/icons-material/History';
import ChangeLogDialog from "components/changeLog/ChangeLogDialog";

export interface DashboardListItemProps {
  dashboard: DashboardDocument;
  permissionList?: Array<string>;
  reloadDashboardList?: () => void;
}

interface DashboardListItemIconProps {
  icon: React.ElementType;
  iconSx?: any
  label: string;
}

function DashboardListItemIcon(props: DashboardListItemIconProps): JSX.Element {
  const theme = useTheme();
  return (
    <Tooltip title={props.label}>
      {React.createElement(props.icon as React.ElementType, {
        sx: {
          ...{ fontSize: "1em", verticalAlign: "middle", ml: 0.5, opacity: 0.5, "&:hover": { opacity: 1 }, color: theme.palette.text.secondary },
          ...props.iconSx
        }
      })}
    </Tooltip>
  )
}

export function DashboardListItem(props: DashboardListItemProps): JSX.Element {
  const user = currentUser();
  const { dashboard, reloadDashboardList } = props;
  const { enqueueSnackbar } = useSnackbar();
  const { i18n } = useTranslation();
  const theme = useTheme();

  const [shareDashboardDialogIsOpen, setShareDashboardDialogIsOpen] = useState<boolean>(false);
  const [editDashboardDialogIsOpen, setEditDashboardDialogIsOpen] = useState<boolean>(false);
  const [reassignDashboardDialogIsOpen, setReassignDashboardDialogIsOpen] = useState<boolean>(false);
  const [duplicateDashboardDialogIsOpen, setDuplicateDashboardDialogIsOpen] = useState<boolean>(false);
  const [showChangeLogDialog, setShowChangeLogDialog] = useState<boolean>(false);
  const [deleteConfirmationIsOpen, setDeleteConfirmationIsOpen] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [dashboardPages, setDashboardPages] = useState<Array<DashboardPage> | undefined>(undefined);
  const [dashboardPagesAreLoading, setDashboardPagesAreLoading] = useState<boolean>(false);

  const menuIsOpen = Boolean(menuAnchorEl);

  const canDuplicate = user?.permissions.includes("create_dashboard");
  const canReassignAndDelete = dashboard?._modifiable && dashboard?._owned_by_you;

  // Only show year if it is not the current year
  const createdAtFormat = (new Date().getFullYear()) === dayjs(dashboard.created_at).year()
    ? { month: "long", day: "numeric" }
    : { year: "numeric", month: "long", day: "numeric" };

  const loadPages = () => {
    // If the pages are already loaded or if the dashboard has no pages, don't try to load them
    if (dashboardPages !== undefined || !dashboard?.data?.pages?.length) {
      return;
    }
    setDashboardPagesAreLoading(true);
    APIRequest(`obj/dashboard/${dashboard.id}/entry`).then((response: any) => {
      const orderedPageIds = dashboard?.data?.pages;
      const allPages = [...response.output];
      // Get the pages sorted by the page id list in the dashboard data
      const orderedPages = orderedPageIds.map((pageId: string) => {
        const page = response.output.find((page: DashboardPage) => page.uuid === pageId);
        if (page) {
          allPages.splice(allPages.indexOf(page), 1);
        }
        return page;
      }).filter((page: DashboardPage | undefined) => page !== undefined) as Array<DashboardPage>;
      setDashboardPages([...orderedPages, ...allPages]);
      setDashboardPagesAreLoading(false);
    }).catch((apiError: APIError) => {
      setDashboardPages([]);
      setDashboardPagesAreLoading(false);
      enqueueSnackbar(`${apiError.message} (${apiError.code} ${apiError.server_message}`, {variant: "error"});
    });
  }

  const deleteDashboard = () => {
    if (!dashboard?.id) {
      return;
    }
    // Delete the dashboard
    APIRequest(`obj/dashboard/${dashboard?.id}`, {}, {
      method: "DELETE",
    }).then(() => {
      if (props.reloadDashboardList) {
        props.reloadDashboardList();
      }
    }).catch((apiError: APIError) => {
      enqueueSnackbar(`${apiError.message} (${apiError.code} ${apiError.server_message}`, {variant: "error"});
    });
  }

  async function handleFavoriteDashboardClicked(dashboard: DashboardDocument): Promise<void> {
    let method = "POST";
    if (dashboard.is_favorite) {
      method = "DELETE";
    }
    try {
      await APIRequest(`favorite-resource/${dashboard.id}`, {}, { method }, true);
      props.reloadDashboardList?.();
      const message = method === "POST" 
        ? i18n.t("dashboard.favorite-success", {name: dashboard.name})
        : i18n.t("dashboard.unfavorite-success", {name: dashboard.name});
      enqueueSnackbar(message, {variant: "success"});
    } catch (e: any) {
      const error = e as APIError;
      enqueueSnackbar(error.message, {variant: "error", persist: true});
    }
  }

  return (
    <Box key={dashboard.id} sx={{ my: 1 }}>
      <Accordion
        disableGutters
        expanded={isExpanded}
        onChange={(event: React.SyntheticEvent, expanded: boolean) => {
          setIsExpanded(expanded);
          if (expanded) { loadPages() }
        }}
      >
        {/* Header */}
        <AccordionSummary
          component="div"
          sx={{ pr: 1 }}
          expandIcon={
            <Tooltip title={isExpanded ? i18n.t("view-less") : i18n.t("view-more")}>
              <ExpandMore data-crumb="DashboardListItem-view-IconButton" aria-label={isExpanded ? i18n.t("view-less") : i18n.t("view-more")} />
            </Tooltip>
          }>
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%"
            }}>
            <Stack direction="column">
              {/* Title */}
              <Link
                component={RouterLink}
                to={`/dashboard/${dashboard.id}`}
                onClick={(e) => e.stopPropagation()}
                sx={{ color: theme.palette.primary.main, "&:hover": { color: theme.palette.primary.dark } }}
              >
                <Typography component="span" variant="h4">
                  {dashboard.name}
                  {dashboard.template_type &&
                    <DashboardListItemIcon icon={FindInPage} label={i18n.t("dashboard.template-dashboard-indicator")} />
                  }
                  {!dashboard.template_type && 
                    <DashboardListItemIcon icon={Dashboard} label={i18n.t("dashboard.freeform-dashboard-indicator")} />
                  }
                  {dashboard._is_shared &&
                    <DashboardListItemIcon icon={IosShare} label={i18n.t("dashboard.shared-dashboard-indicator")} />
                  }
                  {dashboard.access === "public" &&
                    <DashboardListItemIcon icon={Public} label={i18n.t("dashboard.public-dashboard-indicator")} />
                  }
                </Typography>
              </Link>

              {/* Description */}
              {dashboard.description &&
                <Box>
                  <Typography component="span" variant="subtitle1" sx={{ marginRight: "5px", display: "inline" }}>{i18n.t("description")}:</Typography>
                  {dashboard.description.length < 250 ?
                    <Typography sx={{fontWeight: "bold", display: "inline"}} variant="subtitle1">{dashboard.description}</Typography> : 
                    <Typography sx={{fontWeight: "bold", display: "inline"}} variant="subtitle1">{dashboard.description.substring(0, 250)}…</Typography>
                  }
                </Box>
              }

              {/* Timestamp */}
              {dashboard.updated_at && dashboard.updated_at != dashboard.created_at && 
                <Box>
                  <Typography component="span" variant="subtitle1" sx={{ marginRight: "5px", display: "inline-block" }}>{i18n.t("updated")}:</Typography>
                  <Typography sx={{variant: "subtitle1", fontWeight: "bold", display: "inline-block", color: theme.palette.text.secondary}}>
                    <ISORelativeDateTime isoString={dashboard.updated_at} />
                  </Typography>
                </Box>
              }
            </Stack>

            {/* Options */}
            <Stack direction="row" onClick={(e) => e.stopPropagation()} onFocus={(e) => e.stopPropagation()} sx={{
              alignItems: "center"
            }}>
              <Tooltip title={dashboard.is_favorite ? i18n.t("dashboard.button-favorited") : i18n.t("dashboard.button-unfavorited")}>
                <IconButton
                  data-crumb="DashboardListItem-favorite-IconButton"
                  aria-label={i18n.t("dashboard.share-menu")}
                  sx={{color: dashboard.is_favorite ? "gold" : theme.palette.grey[300]}}
                  onClick={(event: React.MouseEvent<HTMLElement>) => {
                    event.preventDefault();
                    handleFavoriteDashboardClicked(dashboard);
                }}>
                  <StarIcon />
                </IconButton>
              </Tooltip>
              {(dashboard?._owned_by_you && dashboard?._modifiable) &&
                <Tooltip title={i18n.t("dashboard.share-menu")}>
                  <IconButton
                    data-crumb="DashboardListItem-share-IconButton"
                    aria-label={i18n.t("dashboard.share-menu")}
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                      event.preventDefault();
                      setShareDashboardDialogIsOpen(true);
                  }}>
                    <IosShare />
                  </IconButton>
                </Tooltip>
              }
              {(dashboard?._owned_by_you && dashboard?._modifiable) &&
                <Tooltip title={i18n.t("dashboard.settings-menu")}>
                  <IconButton
                    data-crumb="DashboardListItem-edit-IconButton"
                    aria-label={i18n.t("dashboard.settings-menu")}
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                      event.preventDefault();
                      setEditDashboardDialogIsOpen(true);
                  }}>
                    <Edit />
                  </IconButton>
                </Tooltip>
              }
              {(canReassignAndDelete || canDuplicate) &&
                <Tooltip title={i18n.t("dashboard.options-menu")}>
                  <IconButton
                    data-crumb="DashboardListItem-options-IconButton"
                    aria-label={i18n.t("dashboard.options-menu")}
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                      event.preventDefault();
                      setMenuAnchorEl(event.currentTarget);
                  }}>
                    <MoreVert/>
                  </IconButton>
                </Tooltip>
              }
            </Stack>
          </Stack>
        </AccordionSummary>

        {/* Content */}
        <AccordionDetails sx={{ py: 0 }}>
          <Divider sx={{ mb: 1 }}/>
          <Typography variant="subtitle1" sx={{
            fontWeight: "bold"
          }}>
            {i18n.t("created")}: <ISODateTime isoString={dashboard.created_at} format={createdAtFormat} hideTooltip={true} />
          </Typography>
          {dashboard._user?.name && 
            <Typography variant="subtitle1" sx={{
              fontWeight: "bold"
            }}>
              {i18n.t("owner")}: <Typography component="span">{dashboard._user.name}</Typography>
            </Typography>
          }
          {dashboard.description &&
            <Typography variant="subtitle1" sx={{
              fontWeight: "bold"
            }}>
              {i18n.t("description")}: <Typography component="span">{dashboard.description}</Typography>
            </Typography>
          }
          {dashboardPagesAreLoading && <TextLoader /> }
          {!!dashboardPages?.length &&
            <Stack direction="row" sx={{
              gap: 1
            }}>
              <Typography variant="subtitle1" sx={{
                fontWeight: "bold"
              }}>
                {i18n.t("nav.pages")}:
              </Typography>
              <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "center"}}>
                {dashboardPages?.map((page: DashboardPage) => (
                  <Box key={page.id}>
                    <Chip 
                      label={page.name} 
                      size="small" 
                      sx={{mr:1, mb:1, borderRadius: "6px"}}
                    />
                  </Box>
                ))}
              </Box>
            </Stack>
          }
        </AccordionDetails>
      </Accordion>

      {/* Dashboard Options Menu */}
      <Menu anchorEl={menuAnchorEl} open={menuIsOpen} onClose={() => setMenuAnchorEl(null)}>
        {canReassignAndDelete &&
          <Tooltip title={i18n.t("dashboard.reassign-dashboard")} followCursor>
            <MenuItem
              data-crumb="DashboardListItem-reassign-IconButton"
              aria-label={i18n.t("dashboard.reassign-dashboard")}
              onClick={(e) => {
                e.preventDefault();
                setMenuAnchorEl(null);
                setReassignDashboardDialogIsOpen(true);
              }}>
              <ListItemIcon><Redo/></ListItemIcon>
              <ListItemText>{i18n.t("reassign")}</ListItemText>
            </MenuItem>
          </Tooltip>
        }
        {canDuplicate &&
          <Tooltip title={i18n.t("dashboard.duplicate-dashboard")} followCursor>
            <MenuItem
              data-crumb="DashboardListItem-duplicate-IconButton"
              aria-label={i18n.t("dashboard.duplicate-dashboard")}
              onClick={(e) => {
                e.preventDefault();
                setMenuAnchorEl(null);
                setDuplicateDashboardDialogIsOpen(true);
              }}>
              <ListItemIcon><ContentCopy/></ListItemIcon>
              <ListItemText>{i18n.t("duplicate")}</ListItemText>
            </MenuItem>
          </Tooltip>
        }
        <Tooltip title="Show the change log." followCursor>
          <MenuItem
            data-crumb="DashboardListItem-history-IconButton"
            aria-label="Change Log"
            onClick={(e) => {
              e.preventDefault();
              setMenuAnchorEl(null);
              setShowChangeLogDialog(true);
            }}>
            <ListItemIcon><HistoryIcon /></ListItemIcon>
            <ListItemText>Change Log</ListItemText>
          </MenuItem>
        </Tooltip>
        {canReassignAndDelete &&
          <Tooltip title={i18n.t("dashboard.delete-dashboard")} followCursor>
            <MenuItem
              data-crumb="DashboardListItem-delete-IconButton"
              aria-label={i18n.t("dashboard.delete-dashboard")}
              onClick={(e) => {
                e.preventDefault();
                setMenuAnchorEl(null);
                setDeleteConfirmationIsOpen(true);
              }}>
              <ListItemIcon sx={{color:theme.palette.error.main}}><DeleteForever /></ListItemIcon>
              <ListItemText sx={{color:theme.palette.error.main}}>{i18n.t("delete")}</ListItemText>
            </MenuItem>
          </Tooltip>
        }
      </Menu>

      {/* Dialogs */}
      {editDashboardDialogIsOpen &&
        <DashboardEditDialog
          setDialogIsOpen = {setEditDashboardDialogIsOpen}
          dashboardId = {dashboard.id}
          onClose = {() => {
            if (reloadDashboardList) {
              reloadDashboardList();
            }
          }}
        />
      }
      {shareDashboardDialogIsOpen &&
        <ResourceActionDialog
          open={shareDashboardDialogIsOpen}
          accountAccess={dashboard.access}
          action="share"
          resourceIds={dashboard.id}
          endpoint="dashboard"
          onClose={() => setShareDashboardDialogIsOpen(false) }
          handleComplete={(wasSuccessful: boolean) => {
            if (wasSuccessful) {
              setShareDashboardDialogIsOpen(false);
              if (reloadDashboardList) {
                reloadDashboardList();
              }
              enqueueSnackbar(i18n.t("resource-editor.save-successful"), { variant: "success" });
            }
          }}
        />
      }
      {reassignDashboardDialogIsOpen &&
        <ResourceActionDialog
          open={reassignDashboardDialogIsOpen}
          accountAccess={dashboard.access}
          action="reassign"
          resourceIds={dashboard.id}
          endpoint="dashboard"
          onClose={() => setReassignDashboardDialogIsOpen(false) }
          handleComplete={(wasSuccessful: boolean) => {
            if (wasSuccessful) {
              setReassignDashboardDialogIsOpen(false);
              if (reloadDashboardList) {
                reloadDashboardList();
              }
              enqueueSnackbar(i18n.t("resource-editor.reassign-successful"), { variant: "success" });
            }
        }} />
      }
      {duplicateDashboardDialogIsOpen &&
        <ResourceActionDialog
          open={duplicateDashboardDialogIsOpen}
          asAdmin={false}
          reassignAccount={false}
          reassignable={false}
          accountAccess={dashboard.access}
          action="duplicate"
          resourceIds={dashboard.id}
          endpoint="dashboard"
          onClose={() => setDuplicateDashboardDialogIsOpen(false) }
          handleComplete={(wasSuccessful: boolean) => {
            if (wasSuccessful) {
              setDuplicateDashboardDialogIsOpen(false);
              if (reloadDashboardList) {
                reloadDashboardList();
              }
              enqueueSnackbar(i18n.t("resource-editor.duplicate-successful"), { variant: "success" });
            }
          }}
        />
      }
      {showChangeLogDialog &&
        <ChangeLogDialog
          onClose={() => setShowChangeLogDialog(false)}
          resourceId={props.dashboard.id}
          resourceType="dashboard"
        />
      }
      <ConfirmationDialog
        isOpen={deleteConfirmationIsOpen}
        title={i18n.t("delete")}
        description={i18n.t("dashboard.delete-confirmation-description", { name: dashboard.name })}
        onClose={(confirmed: boolean) => {
          if (confirmed) {
            deleteDashboard();
          }
          setDeleteConfirmationIsOpen(false);
        }}
      />
    </Box>
  );
}
